// Optimized App.js with React.lazy() for code splitting
import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import MobileStickyButtons from './components/MobileStickyButtons';
import DesktopFloatingButtons from './components/DesktopFloatingButtons';
import { initializeTracking } from './utils/trackingParams';
import { setupPhoneTracking } from './utils/conversionTracking';

// Loading component
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-luxury-bone">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-[#9C8454]"></div>
      <p className="mt-4 text-[#5C5C5C] text-lg font-serif">Loading...</p>
    </div>
  </div>
);

// Lazy load route components for code splitting
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Services = lazy(() => import('./pages/Services'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const ServiceHomeDecor = lazy(() => import('./pages/ServiceHomeDecor'));
const ArchitectureDesign = lazy(() => import('./pages/services/ArchitectureDesign'));
const InteriorDesign = lazy(() => import('./pages/services/InteriorDesign'));
const ProjectManagement = lazy(() => import('./pages/services/ProjectManagement'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const PortfolioArchitecture = lazy(() => import('./pages/PortfolioArchitecture'));
const PortfolioInterior = lazy(() => import('./pages/PortfolioInterior'));
const PortfolioCommercial = lazy(() => import('./pages/PortfolioCommercial'));
const PortfolioDetail = lazy(() => import('./pages/PortfolioDetail'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const BlogArchitecture = lazy(() => import('./pages/BlogArchitecture'));
const BlogInterior = lazy(() => import('./pages/BlogInterior'));
const BlogResidential = lazy(() => import('./pages/BlogResidential'));
const BlogCommercial = lazy(() => import('./pages/BlogCommercial'));
const BestArchitectsGurgaon = lazy(() => import('./pages/BestArchitectsGurgaon'));
const BestArchitectsDelhiNCR = lazy(() => import('./pages/BestArchitectsDelhiNCR'));
const BestArchitectsNoida = lazy(() => import('./pages/BestArchitectsNoida'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const WhatsAppRedirect = lazy(() => import('./pages/WhatsAppRedirect'));

function App() {
  // Initialize tracking on app load
  useEffect(() => {
    initializeTracking();
    
    // Setup phone tracking after DOM loads
    const timer = setTimeout(() => {
      setupPhoneTracking();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <Router>
      <div className="App">
        <Navbar />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Home */}
            <Route path="/" element={<Home />} />
            
            {/* WhatsApp Redirect (for Google Ads sitelink) */}
            <Route path="/wa" element={<WhatsAppRedirect />} />
            
            {/* About & Contact */}
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/thank-you" element={<ThankYou />} />
            
            {/* Services */}
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/services/architecture-design" element={<ArchitectureDesign />} />
            <Route path="/services/interior-design" element={<InteriorDesign />} />
            <Route path="/services/project-management" element={<ProjectManagement />} />
            <Route path="/services/pmc" element={<ProjectManagement />} />
            <Route path="/home-decor" element={<ServiceHomeDecor />} />
            
            {/* Portfolio */}
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/architecture" element={<PortfolioArchitecture />} />
            <Route path="/portfolio/interior" element={<PortfolioInterior />} />
            <Route path="/portfolio/commercial" element={<PortfolioCommercial />} />
            <Route path="/portfolio/:slug" element={<PortfolioDetail />} />
            <Route path="/portfolio/architecture/:slug" element={<PortfolioDetail />} />
            <Route path="/portfolio/interior/:slug" element={<PortfolioDetail />} />
            <Route path="/portfolio/commercial/:slug" element={<PortfolioDetail />} />
            
            {/* Blog */}
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/blog/architecture" element={<BlogArchitecture />} />
            <Route path="/blog/interior" element={<BlogInterior />} />
            <Route path="/blog/residential" element={<BlogResidential />} />
            <Route path="/blog/commercial" element={<BlogCommercial />} />
            
            {/* Location Landing Pages */}
            <Route path="/best-architects-gurgaon" element={<BestArchitectsGurgaon />} />
            <Route path="/best-architects-in-gurgaon" element={<BestArchitectsGurgaon />} />
            <Route path="/best-architects-delhi-ncr" element={<BestArchitectsDelhiNCR />} />
            <Route path="/best-architects-in-delhi-ncr" element={<BestArchitectsDelhiNCR />} />
            <Route path="/best-architects-noida" element={<BestArchitectsNoida />} />
            <Route path="/best-architects-in-noida" element={<BestArchitectsNoida />} />
            
            {/* WhatsApp Sitelink Redirect */}
            <Route path="/wa" element={<WhatsAppRedirect />} />
            
            {/* Legal */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
          </Routes>
        </Suspense>
        <Footer />
        {/* Mobile & Tablet Sticky CTA Buttons */}
        <MobileStickyButtons />
        {/* Desktop Floating CTA Buttons */}
        <DesktopFloatingButtons />
      </div>
    </Router>
  );
}

export default App;
